import { AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import AxiosHelper from 'services/AxiosHelper';

export enum TokenAvailability {
	/**
	 * Token is available globally. This is typically a token that pulls data from settings
	 */
	Global = 'Global',
	/**
	 * Token is available when 'Category' is in scope to pull data from
	 */
	Category = 'Category',
	/**
	 * Token is available when 'Brand' is in scope to pull data from
	 */
	Brand = 'Brand',
	/**
	 * Token is available when 'Product' is in scope to pull data from
	 */
	Product = 'Product',
}

interface GetReplacementTokensResponse {
	tokensByAvailability: { [availability: string]: string[] };
}

let cachedPromise: Promise<AxiosResponse<GetReplacementTokensResponse>> | null = null;

const getReplacementTokens = (): Promise<AxiosResponse<GetReplacementTokensResponse>> => {
	if (!cachedPromise) {
		cachedPromise = AxiosHelper.get<GetReplacementTokensResponse>('/ajax/replacement-tokens');
	}
	return cachedPromise;
};

interface AvailableReplacementTokensProps {
	/**
	 * Specify what availabilities to show.
	 */
	availabilities: TokenAvailability[];

	renderTokens?: (uniqueSortedTokens: string[]) => JSX.Element;
}

export function AvailableReplacementTokens(props: AvailableReplacementTokensProps) {
	const { availabilities, renderTokens } = props;
	const [tokensByAvailability, setTokensByAvailability] = useState<{ [availability: string]: string[] }>({});

	/**
	 * Fires after each scope change
	 */
	useEffect(() => {
		(async () => {
			try {
				const response = await getReplacementTokens();
				setTokensByAvailability(response.data.tokensByAvailability);
			} catch (e) {
				console.error(e);
			}
		})();
	}, []);

	const uniqueTokens: { [token: string]: undefined } = {};

	if (availabilities.length > 0) {
		for (const availability of availabilities) {
			const systemTokens = tokensByAvailability[availability] ?? [];
			for (const systemToken of systemTokens) {
				uniqueTokens[systemToken] = undefined;
			}
		}
	}

	for (const globalToken of tokensByAvailability[TokenAvailability.Global] ?? []) {
		uniqueTokens[globalToken] = undefined;
	}

	const uniqueSortedTokens = Object.keys(uniqueTokens).sort();

	if (renderTokens) {
		return renderTokens(uniqueSortedTokens);
	}
	// Default render
	return (
		<span>
			<strong>Available Tokens:</strong> {uniqueSortedTokens.map((token) => `%%${token}%%`).join(' | ')}
		</span>
	);
}
